import { Box, Grid, Typography } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import { useNavigate } from "react-router-dom";
import { PROFILE_FONT } from "../../constants/layout";
import {
  REPORT_COLORS,
  REPORT_PAGE_INNER_SX,
  REPORT_PAGE_SX,
  REPORT_RADIUS,
  REPORT_SHADOW,
  REPORT_TYPO,
} from "../CustomerReports/mf/mfReportsTheme";

const curationCards = [
  {
    title: "Market Picks",
    description:
      "Select Stock, Mutual Fund, AIF, and PMS highlights for the investor app banner.",
    meta: "4 products",
    icon: ShowChartRoundedIcon,
    path: "/curations/market-picks",
  },
];

const Curations = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ ...REPORT_PAGE_SX, minHeight: "100%" }}>
      <Box sx={REPORT_PAGE_INNER_SX}>
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: PROFILE_FONT,
              fontSize: { xs: 26, sm: 30, md: 32 },
              fontWeight: 700,
              letterSpacing: -0.6,
              color: REPORT_COLORS.primaryText,
              lineHeight: 1.15,
            }}
          >
            Curations
          </Typography>
          <Typography
            sx={{
              ...REPORT_TYPO.pageSubtitle,
              maxWidth: 520,
              mt: 1,
              fontSize: { xs: 13, sm: 14 },
            }}
          >
            Manually selected highlights for the investor app.
          </Typography>
        </Box>

        <Grid container spacing={2.5}>
          {curationCards.map((card) => {
            const Icon = card.icon;

            return (
              <Grid item xs={12} sm={6} md={4} key={card.title}>
                <Box
                  onClick={() => navigate(card.path)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(card.path);
                    }
                  }}
                  sx={{
                    position: "relative",
                    height: "100%",
                    minHeight: 188,
                    p: { xs: 2.5, md: 3 },
                    borderRadius: REPORT_RADIUS.lg,
                    bgcolor: REPORT_COLORS.white,
                    border: `1px solid ${REPORT_COLORS.border}`,
                    boxShadow: REPORT_SHADOW.card,
                    cursor: "pointer",
                    outline: "none",
                    overflow: "hidden",
                    transition:
                      "transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      bgcolor: REPORT_COLORS.button,
                      opacity: 0,
                      transition: "opacity 0.22s ease",
                    },
                    "&:hover": {
                      transform: "translateY(-4px)",
                      borderColor: "rgba(30, 58, 95, 0.35)",
                      boxShadow:
                        "0 12px 32px rgba(30, 58, 95, 0.12), 0 2px 8px rgba(0,0,0,0.04)",
                      "&::before": { opacity: 1 },
                      "& .curation-arrow": {
                        opacity: 1,
                        transform: "translateX(0)",
                        color: REPORT_COLORS.button,
                      },
                      "& .curation-icon": {
                        bgcolor: REPORT_COLORS.button,
                        color: REPORT_COLORS.white,
                      },
                    },
                    "&:focus-visible": {
                      boxShadow: `0 0 0 2px ${REPORT_COLORS.background}, 0 0 0 4px ${REPORT_COLORS.button}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 1.5,
                      mb: 2.25,
                    }}
                  >
                    <Box
                      className="curation-icon"
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        bgcolor: "rgba(30, 58, 95, 0.08)",
                        color: REPORT_COLORS.button,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition:
                          "background-color 0.22s ease, color 0.22s ease",
                      }}
                    >
                      <Icon sx={{ fontSize: 22 }} />
                    </Box>
                    <ArrowForwardRoundedIcon
                      className="curation-arrow"
                      sx={{
                        fontSize: 20,
                        color: REPORT_COLORS.mutedText,
                        opacity: 0.35,
                        transform: "translateX(-4px)",
                        transition:
                          "opacity 0.22s ease, transform 0.22s ease, color 0.22s ease",
                      }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      fontFamily: PROFILE_FONT,
                      fontSize: { xs: 17, md: 18 },
                      fontWeight: 600,
                      letterSpacing: -0.3,
                      color: REPORT_COLORS.primaryText,
                      lineHeight: 1.25,
                      mb: 0.75,
                    }}
                  >
                    {card.title}
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: PROFILE_FONT,
                      fontSize: 13,
                      lineHeight: 1.5,
                      color: REPORT_COLORS.mutedText,
                      mb: 2,
                    }}
                  >
                    {card.description}
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: PROFILE_FONT,
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: 0.4,
                      textTransform: "uppercase",
                      color: "rgba(30, 58, 95, 0.55)",
                    }}
                  >
                    {card.meta}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
};

export default Curations;
